import type { ChainConfig, TokenConfig } from "./types";

export const COTI_TESTNET_CHAIN_ID = 7082400;
export const COTI_MAINNET_CHAIN_ID = 2632500;

const cotiTokenConfigs = (supportedChainIds: number[]): TokenConfig[] => [
  { symbol: "COTI", name: "COTI", icon: "/icons/coti.svg", decimals: 18, isPrivate: false, bridgeAddressKey: "PrivacyBridgeCotiNative", timeout: 1800, supportedChainIds },
  { symbol: "WETH", name: "Wrapped Ether", icon: "/icons/wETH.svg", decimals: 18, isPrivate: false, addressKey: "WETH", bridgeAddressKey: "PrivacyBridgeWETH", timeout: 1800, supportedChainIds },
  { symbol: "WBTC", name: "Wrapped BTC", icon: "/icons/wBTC.svg", decimals: 8, isPrivate: false, addressKey: "WBTC", bridgeAddressKey: "PrivacyBridgeWBTC", timeout: 1800, supportedChainIds },
  { symbol: "USDT", name: "Tether USD", icon: "/icons/usdt.svg", decimals: 6, isPrivate: false, addressKey: "USDT", bridgeAddressKey: "PrivacyBridgeUSDT", timeout: 1800, supportedChainIds },
  { symbol: "USDC.e", name: "Bridged USDC", icon: "/icons/USDC.svg", decimals: 6, isPrivate: false, addressKey: "USDC_E", bridgeAddressKey: "PrivacyBridgeUSDCe", timeout: 1800, supportedChainIds },
  { symbol: "WADA", name: "Wrapped ADA", icon: "/icons/wADA.svg", decimals: 6, isPrivate: false, addressKey: "WADA", bridgeAddressKey: "PrivacyBridgeWADA", timeout: 1800, supportedChainIds },
  { symbol: "gCOTI", name: "gCOTI", icon: "/icons/gcoti.svg", decimals: 18, isPrivate: false, addressKey: "gCOTI", bridgeAddressKey: "PrivacyBridgegCOTI", timeout: 1800, supportedChainIds },
  { symbol: "NIGHT", name: "NIGHT", icon: "https://coti.io/icons/midnight.png", decimals: 6, isPrivate: false, addressKey: "NIGHT", bridgeAddressKey: "PrivacyBridgeNight", timeout: 1800, supportedChainIds },
  { symbol: "p.COTI", name: "p.COTI", icon: "/icons/coti.svg", decimals: 18, isPrivate: true, addressKey: "PrivateCoti", bridgeAddressKey: "PrivacyBridgeCotiNative", timeout: 1800, supportedChainIds },
  { symbol: "p.WETH", name: "p.WETH", icon: "/icons/wETH.svg", decimals: 18, isPrivate: true, addressKey: "p.WETH", bridgeAddressKey: "PrivacyBridgeWETH", timeout: 1800, supportedChainIds },
  { symbol: "p.WBTC", name: "p.WBTC", icon: "/icons/wBTC.svg", decimals: 8, isPrivate: true, addressKey: "p.WBTC", bridgeAddressKey: "PrivacyBridgeWBTC", timeout: 1800, supportedChainIds },
  { symbol: "p.USDT", name: "p.USDT", icon: "/icons/usdt.svg", decimals: 6, isPrivate: true, addressKey: "p.USDT", bridgeAddressKey: "PrivacyBridgeUSDT", timeout: 1800, supportedChainIds },
  { symbol: "p.USDC.e", name: "p.USDC.e", icon: "/icons/USDC.svg", decimals: 6, isPrivate: true, addressKey: "p.USDC_E", bridgeAddressKey: "PrivacyBridgeUSDCe", timeout: 1800, supportedChainIds },
  { symbol: "p.WADA", name: "p.WADA", icon: "/icons/wADA.svg", decimals: 6, isPrivate: true, addressKey: "p.WADA", bridgeAddressKey: "PrivacyBridgeWADA", timeout: 1800, supportedChainIds },
  { symbol: "p.gCOTI", name: "p.gCOTI", icon: "/icons/gcoti.svg", decimals: 18, isPrivate: true, addressKey: "p.gCOTI", bridgeAddressKey: "PrivacyBridgegCOTI", timeout: 1800, supportedChainIds },
  { symbol: "p.NIGHT", name: "p.NIGHT", icon: "https://coti.io/icons/midnight.png", decimals: 6, isPrivate: true, addressKey: "p.NIGHT", bridgeAddressKey: "PrivacyBridgeNight", timeout: 1800, supportedChainIds },
];

export const cotiTestnetChain: ChainConfig = {
  id: COTI_TESTNET_CHAIN_ID,
  hexId: "0x6c11a0",
  name: "COTI Testnet",
  rpcUrl: "https://testnet.coti.io/rpc",
  explorerBaseUrl: "https://testnet.cotiscan.io",
  unlockStrategy: "snap",
  portalStrategy: "coti-bridge",
  // PoD price oracle from pod-ecosystem-integration deployConfig.json (COTI testnet).
  priceOracleAddress: "0x978c0049b16cdca0d23123747114484853093ec8",
  addresses: {
    PrivateCoti: "0xF3FC0A3285a6C297682390b1Db76c3AF130FEb93",
    PrivacyBridgeCotiNative: "0xabB1FC50ebD491c74CE7dfE3bD350bce10f89F2E",
    CotiPriceConsumer: "0xD5EeD24e909AdE249b688671e32dcc013B236B74",
    WETH: "0x8bca4e6bbE402DB4aD189A316137aD08206154FB",
    WBTC: "0x5dBDb2E5D51c3FFab5D6B862Caa11FCe1D83F492",
    USDT: "0x9e961430053cd5AbB3b060544cEcCec848693Cf0",
    USDC_E: "0x63f3D2Cc8F5608F57ce6E5Aa3590A2Beb428D19C",
    WADA: "0xe3E2cd3Abf412c73a404b9b8227B71dE3CfE829D",
    gCOTI: "0x878a42D3cB737DEC9E6c7e7774d973F46fd8ed4C",
    NIGHT: "0x918084eee42832f08b868Ea64Bba2d41A5F360c6",
    PrivacyBridgeNight: "0x025b0E54ae5fac3BF3ca6eDe7bd62fa684c25D29",
    "p.WETH": "0x272968B0fC2710AF20e04d1dDc9a49E2FE5a9caa",
    "p.WBTC": "0xB48119847B7469cbD4a192391287b9c20e6F361F",
    "p.USDT": "0x288b5c8d3419EFD1c9971d0716bb89fAEBC04aBE",
    "p.USDC_E": "0xB4d9f6B49Da5637a713cee8Dd6540f311334Fdea",
    "p.WADA": "0x091Df52B5a7d5EE97132D07Ffd16A21d0eda95b8",
    "p.gCOTI": "0xB71370A6f2Fa9c2184d0a632b6AaBDA034c4B3db",
    "p.NIGHT": "0xfb3A2F7ca2416e7BE312feA60a647f880B840957",
    PrivacyBridgeWETH: "0xF2928e7D17ae11Cea8Ad32340e34F665f618fb92",
    PrivacyBridgeWBTC: "0x8508e8cbDf24084Ab2D1F8b9e5BbF674742b95aC",
    PrivacyBridgeUSDT: "0x90f6576Fb941a152427c4423034e869D88C9faA5",
    PrivacyBridgeUSDCe: "0x8102139805fbFE96c8cBC09be3ae61B5983bCf48",
    PrivacyBridgeWADA: "0x31A7dDA15c6eb9D88934F91d4F43C2F7bb32c767",
    PrivacyBridgegCOTI: "0xcfd4C96dD6ff0EDc9Cd0849de2EF45542A71421f",
  },
  tokens: cotiTokenConfigs([COTI_TESTNET_CHAIN_ID, COTI_MAINNET_CHAIN_ID]),
  walletNetwork: {
    chainId: "0x6c11a0",
    chainName: "COTI Testnet",
    rpcUrls: ["https://testnet.coti.io/rpc"],
    nativeCurrency: { name: "COTI", symbol: "COTI", decimals: 18 },
    blockExplorerUrls: ["https://testnet.cotiscan.io"],
  },
  indexPage: {
    showPodRequestTracker: false,
    amountModalGasLabel: "Estimated Gas Fee",
    amountModalGasSymbol: "COTI",
  },
};

export const cotiMainnetChain: ChainConfig = {
  id: COTI_MAINNET_CHAIN_ID,
  hexId: "0x282b34",
  name: "COTI",
  rpcUrl: "https://mainnet.coti.io/rpc",
  explorerBaseUrl: "https://mainnet.cotiscan.io",
  unlockStrategy: "snap",
  portalStrategy: "coti-bridge",
  // PoD price oracle from pod-ecosystem-integration deployConfig.mainnet.yaml (COTI mainnet).
  priceOracleAddress: "0x216ed3d3b2a1da9372caf6f2747d8f0aabe1de67",
  addresses: {
    // Mainnet private tokens + bridges from coti-contracts
    // scripts/verify-mainnet-private-bridges.cjs
    PrivateCoti: "0xD2F2692B83C3ecDF2EAa0f7c2632BBd46Ae1cC91",
    PrivacyBridgeCotiNative: "0x44D864973392064304dD88E2BDef39fF1ab11b7b",
    CotiPriceConsumer: "0x830c5112E677459648C1aa7Bc5Dd65A36d71Aa4D",
    WETH: "0x639aCc80569c5FC83c6FBf2319A6Cc38bBfe26d1",
    WBTC: "0x8C39B1fD0e6260fdf20652Fc436d25026832bfEA",
    USDT: "0xfA6f73446b17A97a56e464256DA54AD43c2Cbc3E",
    USDC_E: "0xf1Feebc4376c68B7003450ae66343Ae59AB37D3C",
    WADA: "0xe757Ca19d2c237AA52eBb1d2E8E4368eeA3eb331",
    gCOTI: "0x7637C7838EC4Ec6b85080F28A678F8E234bB83D1",
    NIGHT: "",
    PrivacyBridgeNight: "",
    "p.WETH": "0x4727FE8D8450CEBcB142331FAc034Cd8d311f0E5",
    "p.WBTC": "0x65449561257ba5756631Aa0d34f07f6457a319be",
    "p.USDT": "0x42107250C3D385ddfABE69ab6de163702040FeB0",
    "p.USDC_E": "0x63C9a1D05471fc8d47C83968725Dcfdcb5410392",
    "p.WADA": "0x3a8b49aAC1dAD86aa45a75231FbeC5bEb810e416",
    "p.gCOTI": "0x394b3c4328160f000763Ca391D07F902926EDaAc",
    "p.NIGHT": "",
    PrivacyBridgeWETH: "0x7286c83300f0C7131b4006f3cf9F8e44BeB45c13",
    PrivacyBridgeWBTC: "0xc3B7EdEe4f1c0A0bA1AcD341e4982371eC869862",
    PrivacyBridgeUSDT: "0x7685B473DAF1c6DeD815Ca64C6fa18Da2227440D",
    PrivacyBridgeUSDCe: "0x29334fC23ffa2c44AF1b372336C2296591Eadd86",
    PrivacyBridgeWADA: "0xFa2126C07F517013c8d237cc465342da89B96f92",
    PrivacyBridgegCOTI: "0xD4e0d9AB16b48c68044cB6aeA3A089380d6D8cD4",
  },
  tokens: cotiTokenConfigs([COTI_TESTNET_CHAIN_ID, COTI_MAINNET_CHAIN_ID]),
  walletNetwork: {
    chainId: "0x282b34",
    chainName: "COTI",
    rpcUrls: ["https://mainnet.coti.io/rpc"],
    nativeCurrency: { name: "COTI", symbol: "COTI", decimals: 18 },
    blockExplorerUrls: ["https://mainnet.cotiscan.io"],
  },
  indexPage: {
    showPodRequestTracker: false,
    amountModalGasLabel: "Estimated Gas Fee",
    amountModalGasSymbol: "COTI",
  },
};
